import { createRide, deleteRide, modify } from 'store/services/ride'
import { Observable } from 'rxjs/Rx'
import { createListenerMiddleware, createSlice, ListenerEffectAPI, Dispatch } from '@reduxjs/toolkit'
import * as authenticationDuck from 'store/ducks/authentication'
import { intl } from 'i18n'
import { combineEpics } from 'redux-observable'
import { alertSuccess } from 'store/ducks/alert'
import { parseRideCreationPayload } from '../utils/ride'
import { ridesRequest, updateModal } from './rides'
import { deleteFailure } from './vehicle'
import { GlobalState } from './state'

const INITIAL_STATE = {
  creation: {
    loading: false,
    created: false,
    entity: {},
    violations: [],
  },
  vehicleType: null,
  rideType: null,
  driver: null,
  start: {
    address: null,
    fromDateTime: null,
  },
  finish: {
    address: null,
  },
  delete: {
    loading: false,
    edited: false,
    deleted: false,
    entity: {},
    violations: [],
  },
  update: {
    loading: false,
    updated: false,
    entity: {},
    violations: [],
  },
}

const rideSlice = createSlice({
  name: 'ride',
  initialState: INITIAL_STATE,
  reducers: {
    creationRequest: (state, action) => {
      action.payload = parseRideCreationPayload(action.payload)
      state.creation = {
        loading: true,
        created: false,
        entity: {},
        violations: [],
      }
    },
    creationSuccess: (state, action) => {
      state.creation = {
        loading: false,
        created: true,
        entity: action.payload,
        violations: [],
      }
    },

    creationFailure: (state, action) => {
      state.creation = {
        loading: false,
        created: false,
        entity: {},
        violations: action.payload,
      }
    },
    deleteRequest: (state, action) => {
      state.delete = {
        loading: true,
        edited: false,
        deleted: true,
        entity: action.payload,
        violations: [],
      }
    },
    deleteSuccess: (state, action) => {
      state.delete = {
        loading: false,
        edited: false,
        deleted: true,
        entity: {},
        violations: [],
      }
    },
    deleteFailure: (state, action) => {
      state.delete = {
        loading: false,
        edited: false,
        deleted: false,
        entity: action.payload,
        violations: action.payload,
      }
    },
    updateRequest: (state, action) => {
      action.payload = parseRideCreationPayload(action.payload)
      state.update = {
        loading: true,
        updated: false,
        entity: {},
        violations: [],
      }
    },
    updateSuccess: (state, action) => {
      state.update = {
        loading: false,
        updated: true,
        entity: action.payload,
        violations: [],
      }
    },

    updateFailure: (state, action) => {
      state.update = {
        loading: false,
        updated: false,
        entity: {},
        violations: action.payload,
      }
    },
  },
})

export const {
  creationRequest,
  creationSuccess,
  creationFailure,
  deleteRequest,
  deleteSuccess,
  updateRequest,
  updateSuccess,
  updateFailure,
} = rideSlice.actions

export default rideSlice.reducer

export const epic = combineEpics(
  creationRequestEpic,
  sendAlertOnCreateSuccessEpic,
  deleteRequestEpic,
  sendAlertOnDeleteSuccessEpic,
  updateRequestEpic,
  sendAlertOnUpdateSuccessEpic,
)

export function creationRequestEpic(action$) {
  return action$.ofType(creationRequest.type).mergeMap((action) => {
    return (
      Observable.ajax(createRide({ payload: action.payload }))
        .map((res) => creationSuccess(res.response))
        // @ts-ignore
        .catch((err) => {
          if (typeof err === 'object' && (err.response || {}).code === 401) {
            return Observable.of(authenticationDuck.refreshTokenRequest(action))
          }

          try {
            const errors = [...(err.response.violations || []), ...(err.response.errors || [])].map((e) =>
              typeof e === 'string' ? e : e.message,
            )
            return Observable.of(creationFailure(errors))
          } catch (e) {
            return Observable.of(creationFailure([intl.formatMessage({ id: 'ducks.server' })]))
          }
        })
    )
  })
}
export function deleteRequestEpic(action$) {
  return action$.ofType(deleteRequest.type).mergeMap((action) => {
    return (
      Observable.ajax(deleteRide({ payload: action.payload }))
        .map((res) => deleteSuccess(res.response))
        // @ts-ignore
        .catch((err) => {
          if (typeof err === 'object' && (err.response || {}).code === 401) {
            return Observable.of(authenticationDuck.refreshTokenRequest(action))
          }

          try {
            const errors = [...(err.response.violations || []), ...(err.response.errors || [])].map((e) =>
              typeof e === 'string' ? e : e.message,
            )
            return Observable.of(deleteFailure(errors))
          } catch (e) {
            return Observable.of(deleteFailure([intl.formatMessage({ id: 'ducks.server' })]))
          }
        })
    )
  })
}

export function updateRequestEpic(action$) {
  return action$.ofType(updateRequest.type).mergeMap((action) => {
    return (
      Observable.ajax(modify({ params: { id: action.payload.id }, payload: action.payload }))
        .map((res) => updateSuccess(res.response))
        // @ts-ignore
        .catch((err) => {
          if (typeof err === 'object' && (err.response || {}).code === 401) {
            return Observable.of(authenticationDuck.refreshTokenRequest(action))
          }

          try {
            const errors = [...(err.response.violations || []), ...(err.response.errors || [])].map((e) =>
              typeof e === 'string' ? e : e.message,
            )
            return Observable.of(updateFailure(errors))
          } catch (e) {
            return Observable.of(updateFailure([intl.formatMessage({ id: 'ducks.server' })]))
          }
        })
    )
  })
}

export function sendAlertOnCreateSuccessEpic(action$) {
  return action$
    .ofType(creationSuccess.type)
    .mapTo(alertSuccess(intl.formatMessage({ id: 'ducks.ride.creation.success' })))
}

export function sendAlertOnDeleteSuccessEpic(action$) {
  return action$.ofType(deleteSuccess.type).mapTo(alertSuccess(intl.formatMessage({ id: 'ducks.ride.delete.success' })))
}
export function sendAlertOnUpdateSuccessEpic(action$) {
  return action$.ofType(updateSuccess.type).mapTo(alertSuccess(intl.formatMessage({ id: 'ducks.ride.update.success' })))
}

export const selectRideCreation = (state) => state.ride.creation
export const selectRideUpdate = (state) => state.ride.update
export const selectRide = (state) => state.ride

export const rideListener = createListenerMiddleware()

rideListener.startListening({
  predicate: (action) => {
    return (
      action.type === creationSuccess.type || action.type === deleteSuccess.type || action.type === updateSuccess.type
    )
  },
  effect: async (action, listenerApi: ListenerEffectAPI<GlobalState, Dispatch>) => {
    listenerApi.dispatch(updateModal({ action: null, id: null }))
    setTimeout(() => {
      listenerApi.dispatch(ridesRequest({ itemsPerPage: 10 }))
    }, 300)
  },
})
